import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { useState, useEffect, useMemo } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AppLayout from '../components/AppLayout';
import { API_ENDPOINTS } from '../constants/api';
import { formatDate } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';
import { useSuccessSnackbar } from '../contexts/SuccessSnackbarContext';
import { successCopy } from '../utils/successCopy';

interface OutstandingTasks {
  reviewCycleId: string;
  reviewCycleName: string;
  employeeCutoff?: string;
  daysUntilCutoff: number;
  selfReviewPending: TaskEntry[];
  managerPending: TaskEntry[];
  peerFeedbackPending: PeerPending[];
}

interface TaskEntry {
  reviewId: string;
  employeeId: string;
  employeeName: string;
  managerId?: string;
  managerName?: string;
  status: string;
}

interface PeerPending {
  reviewId: string;
  employeeId: string;
  employeeName: string;
  managerId?: string;
  managerName?: string;
  peerId: string;
  peerName: string;
  peerEmail?: string | null;
}

export default function OutstandingTasksPage() {
  const navigate = useNavigate();
  const { cycleId } = useParams<{ cycleId: string }>();
  const { showSuccess } = useSuccessSnackbar();
  const [outstanding, setOutstanding] = useState<OutstandingTasks | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadOutstandingTasks = async () => {
      if (!cycleId) {
        setError('Review cycle ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await apiService.get<OutstandingTasks>(API_ENDPOINTS.REVIEW_CYCLES.OUTSTANDING(cycleId));
        setOutstanding(response.data);
      } catch (err: unknown) {
                setError(getApiErrorMessage(err, couldNotLoad('outstanding tasks')));
      } finally {
        setLoading(false);
      }
    };

    loadOutstandingTasks();
  }, [cycleId]);

  const peerEmailsForChase = useMemo(() => {
    if (!outstanding?.peerFeedbackPending.length) return [];
    const emails = outstanding.peerFeedbackPending
      .map((p) => p.peerEmail?.trim())
      .filter((email): email is string => Boolean(email));
    return [...new Set(emails)];
  }, [outstanding?.peerFeedbackPending]);

  const handleCopyPeerEmails = async () => {
    if (peerEmailsForChase.length === 0) return;
    try {
      await navigator.clipboard.writeText(peerEmailsForChase.join('; '));
      showSuccess(successCopy.copiedToClipboard);
    } catch {
      setError('Could not copy email addresses — try again');
    }
  };

  const handleEmailAllPendingPeers = () => {
    if (peerEmailsForChase.length === 0) return;
    const subject = encodeURIComponent(
      outstanding?.reviewCycleName
        ? `Peer feedback reminder — ${outstanding.reviewCycleName}`
        : 'Peer feedback reminder'
    );
    const body = encodeURIComponent(
      'This is a reminder that your peer feedback is still outstanding for the current review cycle. Please submit it from your Tasks page or the link in your invitation email.'
    );
    window.location.href = `mailto:?bcc=${peerEmailsForChase.map(encodeURIComponent).join(',')}&subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error && !outstanding) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ ml: -1 }} title="Back">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Outstanding Tasks
            </Typography>
          </Box>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ ml: -1 }} title="Back">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Outstanding Tasks
            </Typography>
            {outstanding && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {outstanding.reviewCycleName}
                {outstanding.employeeCutoff && (
                  <span>
                    {' • '}
                    Employee cutoff: {formatDate(outstanding.employeeCutoff)}
                    {outstanding.daysUntilCutoff >= 0 && (
                      <span> ({Math.round(outstanding.daysUntilCutoff)} days remaining)</span>
                    )}
                  </span>
                )}
              </Typography>
            )}
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Follow up on incomplete work for this review cycle: self-reviews, manager reviews, and peer
          feedback that has been invited but not yet submitted. Use the peer list to chase reviewers while
          they can still submit.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {outstanding ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Self-Review Pending */}
            {outstanding.selfReviewPending.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Self-Reviews Pending ({outstanding.selfReviewPending.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Employee</strong></TableCell>
                          <TableCell><strong>Manager</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {outstanding.selfReviewPending.map((task) => (
                          <TableRow key={task.reviewId} hover>
                            <TableCell>{task.employeeName}</TableCell>
                            <TableCell>{task.managerName || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={task.status}
                                size="small"
                                color={task.status === 'Not Started' ? 'error' : 'warning'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                endIcon={<OpenInNewIcon />}
                                onClick={() => navigate(`/reviews/${task.reviewId}`)}
                                sx={{ textTransform: 'none' }}
                              >
                                View Review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Manager Review Pending */}
            {outstanding.managerPending.length > 0 && (
              <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Manager Reviews Pending ({outstanding.managerPending.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Employee</strong></TableCell>
                          <TableCell><strong>Manager</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {outstanding.managerPending.map((task) => (
                          <TableRow key={task.reviewId} hover>
                            <TableCell>{task.employeeName}</TableCell>
                            <TableCell>{task.managerName || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={task.status}
                                size="small"
                                color="warning"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                endIcon={<OpenInNewIcon />}
                                onClick={() => navigate(`/reviews/${task.reviewId}`)}
                                sx={{ textTransform: 'none' }}
                              >
                                View Review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Peer Feedback Pending — always show so HR can see chase status */}
            <Card variant="outlined" sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} id="peer-feedback-pending">
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Peer Feedback Pending ({outstanding.peerFeedbackPending.length})
                    </Typography>
                  </Box>
                  {peerEmailsForChase.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyPeerEmails}
                        sx={{ textTransform: 'none' }}
                      >
                        Copy emails
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<EmailIcon />}
                        onClick={handleEmailAllPendingPeers}
                        sx={{ textTransform: 'none' }}
                      >
                        Email all pending
                      </Button>
                    </Box>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Invited peer reviewers who have not submitted yet, while feedback can still be accepted.
                  Use this list to chase peers during the cycle.
                </Typography>
                {outstanding.peerFeedbackPending.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No outstanding peer feedback for this cycle — either all invited peers have submitted,
                    or peer feedback is no longer open on remaining reviews.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Peer reviewer</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Review for</strong></TableCell>
                          <TableCell><strong>Manager</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {outstanding.peerFeedbackPending.map((task) => (
                          <TableRow key={`${task.reviewId}-${task.peerId}`} hover>
                            <TableCell>{task.peerName}</TableCell>
                            <TableCell>
                              {task.peerEmail ? (
                                <Link href={`mailto:${task.peerEmail}`} underline="hover">
                                  {task.peerEmail}
                                </Link>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{task.employeeName}</TableCell>
                            <TableCell>{task.managerName || 'N/A'}</TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                endIcon={<OpenInNewIcon />}
                                onClick={() => navigate(`/reviews/${task.reviewId}`)}
                                sx={{ textTransform: 'none' }}
                              >
                                Open review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {outstanding.selfReviewPending.length === 0 &&
              outstanding.managerPending.length === 0 &&
              outstanding.peerFeedbackPending.length === 0 && (
                <Alert severity="success">
                  No outstanding self-reviews, manager reviews, or peer feedback for this cycle.
                </Alert>
              )}
          </Box>
        ) : null}
      </Box>
    </AppLayout>
  );
}
